/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  SecondaryMenuBar,
  SocialCarouselCardProps,
  useCurator,
} from '@tishman/components';
import { graphql } from 'gatsby';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';

import { Layout } from '../../layouts';
import HolidaysHeroBlock from '../../blocks/HolidaysHeroBlock';
import HolidaysCrossLinkBlock from '../../blocks/HolidaysCrossLinkBlock';
import HolidaysFeaturedStoriesBlock from '../../blocks/HolidaysFeaturedStoriesBlock';
import {
  ReviewProps,
  TripAdvisorReviews,
} from '../../components/TripAdvisorReviews';
import {
  Description,
  TicketOfferingTicket,
  TicketOfferings,
} from '../../components/TicketOfferings';
import { SocialSection } from '../../components/SocialSection';
import {
  HolidaysFeaturedShops,
  GridDataItem,
} from '../../components/Holidays/HolidaysFeaturedShops';
import { getLocaleLanguage, Locale, usePageContext } from '../../utils';
import {
  TicketOffering,
  getIncludedTicketsArray,
} from '../../utils/get-included-tickets';
import { SanityImage } from '../../components/BuyTickets/types';

import type { PageConfig } from '../../PageConfig';

export const pageConfig: PageConfig = {
  theme: 'Rock Center Holiday',
  pageName: 'The Holidays',
  cta: { to: '/buy-tickets/', label: 'Buy Tickets' },
  secondaryCta: { to: '/contact/private-events/', label: 'Plan An Event' },
};

export default function HolidaysPage({
  data: {
    sanityAttractionHoliday,
    nav: { subnavLabels },
  },
}: {
  data: {
    sanityAttractionHoliday: Queries.SanityAttractionHoliday;
    nav: { subnavLabels: string[] };
  };
}): React.JSX.Element {
  const feedName = 'Rockefeller Center';
  const { locale } = usePageContext<{ locale: Locale }>();

  // ANCHOR LINK LOGIC
  const specialURLForTree =
    '/holidays/rockefeller-center-christmas-tree-lighting';
  const specialURLForTickets = '#holiday-activities';

  const menuLinks =
    subnavLabels && subnavLabels.length > 0
      ? subnavLabels.map((nav: string) => {
          let url;
          if (nav === 'The Christmas Tree') url = specialURLForTree;
          else if (nav === 'BUY TICKETS') url = specialURLForTickets;
          else url = `#${nav.toLowerCase().split(' ').join('-')}`;
          return {
            url: url,
            label: nav,
          };
        })
      : [
          {
            url: '/holidays/rockefeller-center-christmas-tree-lighting/',
            label: 'The Christmas Tree',
          },
          {
            url: '#christmas-at-rockefeller-center',
            label: 'Christmas at Rockefeller Center',
          },
          { url: '#gift-shop', label: 'Gift Shop' },
        ];

  const gridData = sanityAttractionHoliday?.threeColumnGrid?.grid;

  const menuLinkAnchorIds = menuLinks.map((menuLink) =>
    menuLink.url.replace('#', ''),
  );

  // TICKET OFFERINGS ARRAY LOGIC
  const includedTicketsArray = getIncludedTicketsArray(
    (sanityAttractionHoliday?.ticketOfferings ?? []).map(
      (offering): TicketOffering => ({
        title: offering?.title ?? '',
        tickets:
          offering?.tickets?.map((ticket) => ({
            title: ticket?.title ?? '',
            url: {
              url: ticket?.url?.url ?? '',
              caption: ticket?.url?.caption ?? '',
            },
            image: ticket?.image as SanityImage,
            description: ticket?.description as unknown as Description[],
            slugId: ticket?.slugId ?? '',
          })) ?? [],
      }),
    ),
    sanityAttractionHoliday,
  );

  // SOCIAL SECTION LOGIC
  const useCuratorResult = useCurator({ feedName });

  const cards = useMemo(() => {
    if (useCuratorResult.error) return null;

    return useCuratorResult.data.reduce<SocialCarouselCardProps[]>((acc, d) => {
      if (!d) throw new Error('Expected valid data');
      if (!d.id) {
        console.warn('Expected valid Curator post id');
        return acc;
      }
      if (!d.text) {
        console.warn(`Expected valid caption, Curator post id: ${d.id}`);
        return acc;
      }
      if (!d.image) {
        console.warn(`Expected valid image, Curator post id: ${d.id}`);
        return acc;
      }
      if (!d.source_created_at) {
        console.warn(`Expected valid creation time, Curator post id: ${d.id}`);
        return acc;
      }

      acc.push({
        id: `${d.id}`,
        caption: d.text,
        image: d.image,
        url: d.url,
      });
      return acc;
    }, []);
  }, [useCuratorResult]);

  return (
    <Layout theme="Rock Center Navy">
      <Helmet htmlAttributes={{ lang: getLocaleLanguage(locale) }}>
        <script
          data-oljs="P3643-808E-77B4-49CA"
          referrerPolicy="no-referrer-when-downgrade"
          src="https://xapis.onelink-edge.com/moxie.min.js"
          type="text/javascript"
        ></script>
      </Helmet>
      <SecondaryMenuBar
        cta={pageConfig.cta}
        links={menuLinks}
        sticky
        threshold={0.5}
        title="Explore The Holidays"
      />

      <HolidaysHeroBlock id={menuLinkAnchorIds[0]} />

      {/* HOLIDAY ATTRACTIONS CAROUSEL */}
      <TicketOfferings
        id={menuLinkAnchorIds[1]}
        includedTicketsArray={includedTicketsArray as TicketOfferingTicket[]}
        theme="Rock Center Navy"
        title="Holiday Attractions"
      />

      {/* Trip Advisor */}
      {sanityAttractionHoliday?.tripadvisorsReview &&
        sanityAttractionHoliday.tripadvisorsReview.length > 0 && (
          <TripAdvisorReviews
            reviews={
              sanityAttractionHoliday?.tripadvisorsReview as unknown as ReviewProps[]
            }
            theme="Top of the Rock Blue"
          />
        )}

      {/* FEATURED SHOPS */}
      {gridData && (
        <HolidaysFeaturedShops
          gridData={[...gridData] as GridDataItem[]}
          id={menuLinkAnchorIds[2]}
          sectionLink={
            sanityAttractionHoliday?.threeColumnGrid?.sectionLink ?? ''
          }
          sectionTitle={
            sanityAttractionHoliday?.threeColumnGrid?.sectionTitle ?? ''
          }
        />
      )}

      {/* Social Section */}
      <SocialSection
        cards={cards}
        copy={
          'We’d love to see the pics from your visit to Rockefeller Center. Be sure to share them!'
        }
        hashtag={'#RockefellerCenter'}
        theme="Rock Center Cream"
        title={'Share your holiday joy!'}
      />

      {/* TWO COLUMN */}
      <HolidaysCrossLinkBlock theme="Rock Center" />

      {/* The Center Magazine */}
      <HolidaysFeaturedStoriesBlock theme="Rock Center Green" />
    </Layout>
  );
}

export const AttractionHolidaysQuery = graphql`
  query AttractionHolidays {
    nav: sanityAttractionHoliday(
      _id: { eq: "theHolidays" }
      _type: { eq: "attraction.holiday" }
    ) {
      subnavLabels
      heroCTA {
        title
        bodyCopy
      }
    }

    sanityAttractionHoliday(
      _id: { eq: "theHolidays" }
      _type: { eq: "attraction.holiday" }
    ) {
      includedTickets {
        slugId
        categoryTitle
        order
      }
      tripadvisorsReview {
        review
        source
      }
      threeColumnGrid {
        grid {
          title
          description
          imageOrVideo {
            image {
              asset {
                gatsbyImageData(
                  layout: CONSTRAINED
                  placeholder: BLURRED
                  formats: [AUTO, WEBP]
                )
                altText
              }
            }
          }
          buttonText
          buttonLink
        }
        sectionTitle
        sectionLink
      }
      ticketOfferings {
        title
        tickets {
          title
          url {
            url
            caption
          }
          image {
            alt
            asset {
              gatsbyImageData(
                layout: CONSTRAINED
                placeholder: BLURRED
                formats: [AUTO, WEBP]
              )
            }
          }
          description {
            children {
              text
            }
          }
          slugId
        }
      }
    }

    meta: sanityAttractionHoliday {
      seo {
        title: metaTitle
        description: metaDescription
        canonical: metaCanonicalUrl
        ogImage: metaImage {
          asset {
            gatsbyImageData(
              layout: FULL_WIDTH
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
      }
    }
  }
`;
